import React, { useState, useEffect } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../config/firebase';
import './styles.css';

/**
 * Admin Debug Panel
 * Shows real-time simulation status and logs for debugging
 */
export const AdminDebugPanel = () => {
  const [logs, setLogs] = useState([]);
  const [simulationStatus, setSimulationStatus] = useState(null);
  const [params, setParams] = useState(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Listen to simulation params
    const paramsRef = doc(db, 'adminSetlist', 'simulationParams');
    const unsubscribeParams = onSnapshot(paramsRef, (snap) => {
      if (snap.exists()) {
        setParams(snap.data());
        addLog('📊 Simulation params loaded', 'info');
      }
    });

    // Listen to current setlist for updates
    const setlistRef = doc(db, 'adminSetlist', 'currentSetlist');
    const unsubscribeSetlist = onSnapshot(setlistRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setSimulationStatus({
          lastUpdate: data.updatedAt,
          updatedBy: data.updatedBy,
          isManualOverride: data.isManualOverride,
          songCount: data.songs?.length || 0,
          lastAdminUpdate: data.lastAdminUpdate
        });

        if (data.updatedBy === 'simulation-advanced') {
          addLog(`✅ Simulation update complete - ${data.songs?.length || 0} songs`, 'success');
        } else if (data.updatedBy?.includes('admin') || data.updatedBy?.includes('custom')) {
          addLog(`👤 Admin update by ${data.updatedBy}`, 'admin');
        }
      }
    });

    // Intercept console methods for admin panel
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;

    console.log = (...args) => {
      originalLog.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      
      // Only capture simulation-related logs
      if (message.includes('🎲') || message.includes('simulation') || 
          message.includes('Simulation') || message.includes('🎯') ||
          message.includes('⏸️') || message.includes('⏰')) {
        addLog(message, 'info');
      }
    };

    console.error = (...args) => {
      originalError.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      addLog(message, 'error');
    };

    console.warn = (...args) => {
      originalWarn.apply(console, args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      addLog(message, 'warning');
    };

    return () => {
      unsubscribeParams();
      unsubscribeSetlist();
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, []);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev.slice(-49), { timestamp, message, type }].slice(-50)); // Keep last 50 logs
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const timeSinceUpdate = () => {
    if (!simulationStatus?.lastUpdate) return 'N/A';
    const diff = Date.now() - new Date(simulationStatus.lastUpdate).getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    return `${minutes}m ${seconds % 60}s ago`;
  };

  return (
    <div className="admin-debug-panel">
      <div className="debug-header" onClick={() => setExpanded(!expanded)}>
        <div className="debug-title">
          <span className="debug-icon">🔍</span>
          <h3>Simulation Debug Panel</h3>
          <span className={`status-indicator ${simulationStatus?.isManualOverride ? 'paused' : 'active'}`}>
            {simulationStatus?.isManualOverride ? '⏸️ Paused' : '▶️ Active'}
          </span>
        </div>
        <button className="toggle-btn">{expanded ? '▼' : '▶'}</button>
      </div>

      {expanded && (
        <div className="debug-content">
          {/* Status Section */}
          <div className="debug-section status-section">
            <h4>📊 Status</h4>
            <div className="status-grid">
              <div className="status-item">
                <span className="label">Last Update:</span>
                <span className="value">{timeSinceUpdate()}</span>
              </div>
              <div className="status-item">
                <span className="label">Updated By:</span>
                <span className="value">{simulationStatus?.updatedBy || 'N/A'}</span>
              </div>
              <div className="status-item">
                <span className="label">Songs:</span>
                <span className="value">{simulationStatus?.songCount || 0}</span>
              </div>
              <div className="status-item">
                <span className="label">Mode:</span>
                <span className="value">{params?.rankingMode || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Parameters Section */}
          {params && (
            <div className="debug-section params-section">
              <h4>⚙️ Parameters</h4>
              <div className="params-grid">
                <div className="param-item">
                  <span className="label">Update Interval:</span>
                  <span className="value">{(params.updateInterval / 1000).toFixed(0)}s</span>
                </div>
                <div className="param-item">
                  <span className="label">Bias Power:</span>
                  <span className="value">{params.biasPower?.toFixed(2) || 'N/A'}</span>
                </div>
                <div className="param-item">
                  <span className="label">Mean Reversion:</span>
                  <span className="value">{params.meanReversion?.toFixed(3) || 'N/A'}</span>
                </div>
                <div className="param-item">
                  <span className="label">Volatility:</span>
                  <span className="value">{params.volatility?.toFixed(3) || 'N/A'}</span>
                </div>
              </div>
            </div>
          )}

          {/* Logs Section */}
          <div className="debug-section logs-section">
            <div className="logs-header">
              <h4>📝 Recent Activity</h4>
              <button onClick={clearLogs} className="clear-logs-btn">Clear</button>
            </div>
            <div className="logs-container">
              {logs.length === 0 ? (
                <div className="no-logs">No activity yet...</div>
              ) : (
                logs.map((log, index) => (
                  <div key={index} className={`log-entry ${log.type}`}>
                    <span className="log-time">{log.timestamp}</span>
                    <span className="log-message">{log.message}</span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDebugPanel;
